import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { BookingState, GetCloseWorkTaskDialogueDTO } from '../../api/api';
import TaskService from '../../services/TaskService';
import { log } from '../../utils/logging/log';
import Alert from '../alert/Alert';
import Typography from '../typography/Typography';

// 'task' stops the task itself, which cancels its planned bookings as well. 'day' ends today's work on
// a task that continues, so only the bookings already under way are closed.
export type CloseScope = 'task' | 'day';

interface Props {
  taskId?: number;
  scope: CloseScope;
}

const bookingStateText = (state?: BookingState) => (state === BookingState.OnRoute ? 'på rute' : 'igang');

const headingText = (scope: CloseScope) =>
  scope === 'task'
    ? 'Når du stopper opgaven, så lukkes øvrige bookinger på den. Det er helt okay, hvis opgaven er færdig.'
    : 'Når du afslutter dagens arbejde, så lukkes øvrige åbne bookinger på opgaven. Det er helt okay, hvis I er færdige for i dag.';

// Closing settles the bookings of the other technicians too, and they only find out afterwards. This
// tells "første mand" which bookings go with the action - it is information, not a problem.
const CloseTaskBookingsWarning = (props: Props) => {
  const { taskId, scope } = props;
  const [dialogue, setDialogue] = useState<GetCloseWorkTaskDialogueDTO>();

  useEffect(() => {
    if (!taskId) return;
    TaskService.getCloseTaskDialogue(taskId)
      .then((res) => setDialogue(res))
      .catch((err) => log(err));
  }, [taskId]);

  const openBookings = dialogue?.openBookings ?? [];
  const plannedBookingCount = scope === 'task' ? dialogue?.plannedBookingCount ?? 0 : 0;

  if (openBookings.length === 0 && plannedBookingCount === 0) return null;

  return (
    <Alert severity="action" padding="12px" margin="8px 0 0 0">
      <Typography>{headingText(scope)}</Typography>
      <BookingList>
        {openBookings.map((booking) => (
          <li key={booking.bookingId}>
            {booking.technician?.name} ({bookingStateText(booking.state)}) afsluttes
          </li>
        ))}
        {plannedBookingCount > 0 && (
          <li>
            {plannedBookingCount} {plannedBookingCount === 1 ? 'planlagt booking' : 'planlagte bookinger'} annulleres
          </li>
        )}
      </BookingList>
      <Typography>
        Du kan overdrage ansvaret for opgaven til en anden tekniker, hvis du kun ønsker at lukke din booking for i dag.
      </Typography>
    </Alert>
  );
};

const BookingList = styled.ul`
  margin: 8px 0;
  padding-left: 20px;
  ${(props) => ({ ...props.theme.typography.p })};
`;

export default CloseTaskBookingsWarning;
